import { AlertTriangle, Boxes, CalendarDays, Camera, CheckCircle2, ClipboardList, CloudOff, Hand, ImagePlus, Info, MessageSquareText, Play, Ruler, Timer, Trash2, Undo2, UserRound, Users, Wrench, ZoomIn } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError, apiRequest } from '../api/client'
import type { ChecklistResult, ChecklistResultInput, CompletionIssue, Defect, DefectPriority, EquipmentComponent, ExecutorGroupRef, Photo, User, UserRef, WorkOrderDetail } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { AssigneeOptions } from '../components/AssigneeOptions'
import { AuthImage } from '../components/AuthImage'
import { PhotoViewer, type ViewerPhoto } from '../components/PhotoViewer'
import { useToast } from '../components/ToastProvider'
import { WorkOrderBackLink } from '../components/WorkOrderNavigation'
import { Badge, Button, CachedNotice, ConfirmDialog, EmptyState, ErrorState, LoadingState, Modal } from '../components/ui'
import { useOfflineQuery } from '../hooks/useOfflineQuery'
import { putCached } from '../offline/db'
import { executeOrQueueFile, executeOrQueueJson } from '../offline/outbox'
import { compressPhoto } from '../utils/image'
import { useSync } from '../offline/SyncContext'
import { answerLabel, defectPriorityLabel, defectPriorityTone, defectStatusLabel, defectStatusTone, errorMessage, formatDate, resultTypeLabel, workOrderStatusLabel, workOrderStatusTone } from '../utils/format'
import {
  applyChecklistInputs,
  applyQueuedChanges,
  CACHE_PERSIST_DELAY_MS,
  canStartWorkOrder,
  choiceLabels,
  completionIssuesFor,
  defectForResult,
  displayDecimal,
  formatDuration,
  inputFromResult,
  isChoiceType,
  isNumericType,
  isPendingId,
  isWorkOrderFinished,
  isWorkOrderInProgress,
  issueHint,
  multiSelectValues,
  normalizeDecimalInput,
  normHint,
  pendingDefectForResult,
  queuedWorkOrderChanges,
  recountDetail,
  RESULT_SAVE_DELAY_MS,
  resultValueLabel,
  validatePhotoFile,
  workOrderCacheKey,
  type PendingDefect,
  type PendingPhoto,
  type TimedInput,
  displayTitle,
  systemCode,
} from './workOrderHelpers'

interface Draft {
  input: ChecklistResultInput
  version: number
  at: string
  position: number
  resultType: ChecklistResult['resultType']
}

interface DefectBody {
  title: string
  resultId?: string
  componentId?: string
  description?: string
  priority: DefectPriority
  assigneeId?: string
  dueDate?: string
  photoIds?: string[]
}

interface MeasurementBody {
  title: string
  unit: string | null
  minValue: string | null
  maxValue: string | null
  value: string | null
  comment: string | null
}

const QUEUED_MESSAGE = 'Сохранено на устройстве, отправится при подключении'
const DEFECT_PRIORITIES: DefectPriority[] = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL']

function outgoingInput(draft: Draft): ChecklistResultInput | null {
  const input: ChecklistResultInput = { ...draft.input }
  if (typeof input.comment === 'string') input.comment = input.comment.trim() || null
  if (isNumericType(draft.resultType)) {
    const normalized = normalizeDecimalInput(typeof input.value === 'string' ? input.value : null)
    if (!normalized.valid) return null
    input.value = normalized.value
  } else if (typeof input.value === 'string') {
    input.value = input.value.trim() || null
  }
  return input
}

function isWorkOrderDetail(value: unknown): value is WorkOrderDetail {
  return Boolean(value) && typeof value === 'object' && 'checklist' in (value as Record<string, unknown>) && Array.isArray((value as WorkOrderDetail).checklist)
}

export function WorkOrderDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { user, hasAnyRole } = useAuth()
  const toast = useToast()
  const sync = useSync()
  const planner = hasAnyRole('ROLE_PLANNER')
  const admin = hasAnyRole('ROLE_ADMIN')
  const cacheKey = workOrderCacheKey(id)
  const resource = useOfflineQuery<WorkOrderDetail>(cacheKey, `/work-orders/${id}`, { enabled: Boolean(id), maxAgeMs: 60_000 })

  const [override, setOverride] = useState<{ source: WorkOrderDetail | null; detail: WorkOrderDetail } | null>(null)
  const serverDetail = override && override.source === resource.data ? override.detail : resource.data
  const [drafts, setDrafts] = useState<ReadonlyMap<string, Draft>>(() => new Map())
  const [saving, setSaving] = useState(0)
  const [starting, setStarting] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [releasing, setReleasing] = useState(false)
  const [releaseOpen, setReleaseOpen] = useState(false)
  const [takingOver, setTakingOver] = useState(false)
  const [takeOverOpen, setTakeOverOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [completeOpen, setCompleteOpen] = useState(false)
  const [measurementOpen, setMeasurementOpen] = useState(false)
  const [defectTarget, setDefectTarget] = useState<{ item: ChecklistResult | null } | null>(null)
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null)
  const [deletingPhoto, setDeletingPhoto] = useState(false)
  const [photoViewer, setPhotoViewer] = useState<{ workOrderId: string; photos: ViewerPhoto[]; initialId: string } | null>(null)

  useEffect(() => () => {
    for (const photo of photoViewer?.photos ?? []) {
      if (photo.local && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(photo.src)
    }
  }, [photoViewer])

  const draftsRef = useRef(drafts)
  const serverDetailRef = useRef(serverDetail)
  const resourceDataRef = useRef(resource.data)
  const timersRef = useRef(new Map<string, number>())
  const flushRef = useRef<(resultId: string) => Promise<void>>(async () => undefined)
  const photoTargetRef = useRef<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastQueuedToastRef = useRef(0)

  useEffect(() => { draftsRef.current = drafts }, [drafts])
  useEffect(() => { serverDetailRef.current = serverDetail }, [serverDetail])
  useEffect(() => { resourceDataRef.current = resource.data }, [resource.data])
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      // При уходе со страницы отправляем ещё не отправленные ответы, не дожидаясь паузы.
      for (const [resultId, timer] of timers) {
        window.clearTimeout(timer)
        void flushRef.current(resultId)
      }
      timers.clear()
    }
  }, [])

  const actor = useMemo<UserRef | null>(() => (user ? { id: user.id, fullName: user.fullName, email: user.email } : null), [user])
  const queued = useMemo(() => queuedWorkOrderChanges(id, sync.queue), [id, sync.queue])
  const draftInputs = useMemo<TimedInput[]>(() => [...drafts.values()].map((draft) => ({ input: draft.input, at: draft.at })), [drafts])
  const detail = useMemo(
    () => (serverDetail ? applyChecklistInputs(applyQueuedChanges(serverDetail, queued, actor), draftInputs, actor) : null),
    [serverDetail, queued, draftInputs, actor],
  )
  const persistable = useMemo(
    () => (serverDetail && (draftInputs.length > 0 || queued.results.length > 0 || queued.started || queued.claimed)
      ? applyChecklistInputs(applyQueuedChanges(serverDetail, queued, actor, { persistable: true }), draftInputs, actor)
      : null),
    [serverDetail, queued, draftInputs, actor],
  )

  useEffect(() => {
    if (!user || !persistable) return
    const timer = window.setTimeout(() => {
      void putCached(user.id, cacheKey, { data: persistable }).catch(() => undefined)
    }, CACHE_PERSIST_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [cacheKey, persistable, user])

  const status = detail?.status
  // Свободное задание объекта: исполнитель может закрепить его за собой («Взять в работу») или сразу начать — старт закрепляет задание.
  const poolMember = Boolean(detail && !detail.assignee && detail.canClaim && canStartWorkOrder(status))
  const canExecute = Boolean(detail && user && (detail.assignee?.id === user.id || (!detail.assignee && planner) || poolMember || admin))
  const editable = Boolean(detail && isWorkOrderInProgress(status) && canExecute)
  const startable = Boolean(detail && canStartWorkOrder(status) && canExecute)
  const finished = isWorkOrderFinished(status)
  // Работа из ППР без программы ТО: чек-листа нет, «измерения вне программы» его не заменяют (исполнители вписывали туда «Вибрации нет»).
  const programMissing = Boolean(detail && detail.planItemId && !detail.program && !finished)
  const canEditGroup = planner && !finished && navigator.onLine
  const canAssign = hasAnyRole('ROLE_ADMIN', 'ROLE_MANAGER') && !finished && navigator.onLine
  // Исполнитель может вернуть взятое по ошибке задание, пока ничего не заполнил; коллега по группе — продолжить чужое (пересменка).
  const mine = Boolean(detail && user && detail.assignee?.id === user.id)
  const releasable = Boolean(detail && mine && !planner && (status === 'ASSIGNED' || (status === 'IN_PROGRESS' && detail.answeredCount === 0 && detail.photos.length === 0 && queued.photos.length === 0)))
  const foreign = Boolean(detail && user && detail.assignee && detail.assignee.id !== user.id && !planner && !admin && !finished)
  const canCreateDefect = editable || (planner && Boolean(detail) && status !== 'CANCELLED')
  const issues = useMemo(() => (detail ? completionIssuesFor(detail, queued.photos, queued.defects) : []), [detail, queued.photos, queued.defects])
  const completable = editable && issues.length === 0
  const photoMissing = issues.some((issue) => issue.code === 'WORK_ORDER_PHOTO_REQUIRED')
  const issuesByResult = useMemo(() => {
    const map = new Map<string, CompletionIssue[]>()
    for (const issue of issues) {
      if (issue.resultId) map.set(issue.resultId, [...(map.get(issue.resultId) ?? []), issue])
    }
    return map
  }, [issues])

  function notifyQueued(message = QUEUED_MESSAGE) {
    const now = Date.now()
    if (now - lastQueuedToastRef.current < 4000) return
    lastQueuedToastRef.current = now
    toast.show(message, 'info')
  }

  function adoptServerDetail(next: WorkOrderDetail) {
    setOverride({ source: resourceDataRef.current, detail: next })
    if (user) void putCached(user.id, cacheKey, { data: next }).catch(() => undefined)
  }

  function dropDraft(resultId: string, version: number) {
    const current = draftsRef.current.get(resultId)
    if (!current || current.version !== version) return
    const next = new Map(draftsRef.current)
    next.delete(resultId)
    draftsRef.current = next
    setDrafts(next)
  }

  async function flush(resultId: string) {
    timersRef.current.delete(resultId)
    const draft = draftsRef.current.get(resultId)
    const base = serverDetailRef.current
    if (!draft || !user || !base) return
    const payload = outgoingInput(draft)
    if (!payload) return
    setSaving((count) => count + 1)
    try {
      const result = await executeOrQueueJson<WorkOrderDetail>({
        userId: user.id,
        label: `Чек-лист ${base.number}: пункт ${draft.position}`,
        method: 'POST',
        path: `/work-orders/${id}/results`,
        body: { items: [payload] },
      })
      // Сохранённое значение обрезано по краям; пока в поле есть завершающий пробел (человек продолжает печатать),
      // черновик остаётся, иначе после сохранения пропадал бы пробел между словами.
      const typing = (typeof draft.input.comment === 'string' && draft.input.comment !== draft.input.comment.trim())
        || (typeof draft.input.value === 'string' && draft.input.value !== draft.input.value.trim())
      if (result.queued) {
        await sync.refreshQueue()
        if (!typing) dropDraft(resultId, draft.version)
        notifyQueued()
      } else {
        if (isWorkOrderDetail(result.data)) adoptServerDetail(result.data)
        if (!typing) dropDraft(resultId, draft.version)
      }
    } catch (error) {
      dropDraft(resultId, draft.version)
      toast.show(errorMessage(error), 'error')
      void resource.refresh()
    } finally {
      setSaving((count) => count - 1)
    }
  }
  useEffect(() => { flushRef.current = flush })

  function scheduleFlush(resultId: string) {
    const existing = timersRef.current.get(resultId)
    if (existing !== undefined) window.clearTimeout(existing)
    timersRef.current.set(resultId, window.setTimeout(() => void flushRef.current(resultId), RESULT_SAVE_DELAY_MS))
  }

  function updateItem(item: ChecklistResult, patch: Partial<ChecklistResultInput>) {
    if (!user || isPendingId(item.id)) return
    const existing = draftsRef.current.get(item.id)
    const input: ChecklistResultInput = { ...inputFromResult(item), ...patch, id: item.id, position: item.position }
    const draft: Draft = { input, version: (existing?.version ?? 0) + 1, at: new Date().toISOString(), position: item.position, resultType: item.resultType }
    const next = new Map(draftsRef.current)
    next.set(item.id, draft)
    draftsRef.current = next
    setDrafts(next)
    scheduleFlush(item.id)
  }

  async function claimWork() {
    if (!user || !detail) return
    setClaiming(true)
    try {
      const result = await executeOrQueueJson<WorkOrderDetail>({ userId: user.id, label: `Взятие в работу ${detail.number}`, method: 'POST', path: `/work-orders/${id}/claim` })
      if (result.queued) {
        await sync.refreshQueue()
        notifyQueued('Задание закреплено за вами на устройстве и отправится при подключении')
      } else {
        if (isWorkOrderDetail(result.data)) adoptServerDetail(result.data)
        else void resource.refresh()
        toast.show('Задание закреплено за вами. Другие исполнители его больше не видят', 'success')
      }
    } catch (error) {
      toast.show(errorMessage(error), 'error')
      void resource.refresh()
    } finally {
      setClaiming(false)
    }
  }

  async function releaseWork() {
    if (!user || !detail) return
    setReleasing(true)
    try {
      const result = await executeOrQueueJson<WorkOrderDetail>({ userId: user.id, label: `Возврат задания ${detail.number}`, method: 'POST', path: `/work-orders/${id}/release` })
      setReleaseOpen(false)
      if (result.queued) {
        await sync.refreshQueue()
        notifyQueued('Возврат задания сохранён на устройстве и отправится при подключении')
      } else {
        if (isWorkOrderDetail(result.data)) adoptServerDetail(result.data)
        else void resource.refresh()
        toast.show('Задание снова свободно: его увидят другие исполнители группы', 'success')
      }
    } catch (error) {
      toast.show(errorMessage(error), 'error')
      void resource.refresh()
    } finally {
      setReleasing(false)
    }
  }

  async function takeOverWork() {
    if (!user || !detail) return
    setTakingOver(true)
    try {
      const result = await executeOrQueueJson<WorkOrderDetail>({ userId: user.id, label: `Перехват задания ${detail.number}`, method: 'POST', path: `/work-orders/${id}/take-over` })
      setTakeOverOpen(false)
      if (result.queued) {
        await sync.refreshQueue()
        notifyQueued('Перехват задания сохранён на устройстве и отправится при подключении')
      } else {
        if (isWorkOrderDetail(result.data)) adoptServerDetail(result.data)
        else void resource.refresh()
        toast.show('Задание закреплено за вами. Ответы и фото коллеги сохранены', 'success')
      }
    } catch (error) {
      toast.show(errorMessage(error), 'error')
      void resource.refresh()
    } finally {
      setTakingOver(false)
    }
  }

  async function startWork() {
    if (!user || !detail) return
    setStarting(true)
    try {
      const result = await executeOrQueueJson<WorkOrderDetail>({ userId: user.id, label: `Начало работы ${detail.number}`, method: 'POST', path: `/work-orders/${id}/start` })
      if (result.queued) {
        await sync.refreshQueue()
        notifyQueued('Начало работы сохранено на устройстве. Заполняйте чек-лист — всё отправится при подключении')
      } else {
        if (isWorkOrderDetail(result.data)) adoptServerDetail(result.data)
        else void resource.refresh()
        toast.show('Работа начата. Заполняйте чек-лист по ходу выполнения', 'success')
      }
    } catch (error) {
      toast.show(errorMessage(error), 'error')
    } finally {
      setStarting(false)
    }
  }

  function openPhotoPicker(resultId: string | null) {
    photoTargetRef.current = resultId
    fileInputRef.current?.click()
  }

  function onPhotoChosen(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const problem = validatePhotoFile(file)
    if (problem) {
      toast.show(problem, 'error')
      return
    }
    void uploadPhoto(file, photoTargetRef.current)
  }

  async function uploadPhoto(file: File, resultId: string | null) {
    const base = serverDetailRef.current
    if (!user || !base) return
    const item = resultId ? base.checklist.find((candidate) => candidate.id === resultId) ?? null : null
    setUploading(true)
    try {
      const result = await executeOrQueueFile<Photo>({
        userId: user.id,
        label: item ? `Фото ${base.number}: пункт ${item.position}` : `Фото ${base.number}`,
        method: 'POST',
        path: `/work-orders/${id}/photos`,
        file: await compressPhoto(file),
        fields: resultId ? { resultId } : undefined,
      })
      if (result.queued) {
        await sync.refreshQueue()
        notifyQueued('Фото сохранено на устройстве, отправится при подключении')
      } else if (result.data && typeof result.data === 'object' && 'url' in result.data) {
        const photo = result.data
        const current = serverDetailRef.current ?? base
        const checklist = current.checklist.map((entry) => (entry.id === photo.checklistResultId && entry.resultType === 'PHOTO' && !entry.answer
          ? { ...entry, answer: 'NORMAL' as const, answered: true, answeredAt: photo.uploadedAt, answeredBy: actor ?? entry.answeredBy ?? null }
          : entry))
        adoptServerDetail(recountDetail({ ...current, checklist, photos: [...current.photos, photo] }))
        toast.show('Фото добавлено', 'success')
      }
    } catch (error) {
      toast.show(errorMessage(error), 'error')
    } finally {
      setUploading(false)
    }
  }

  async function deletePhoto(photo: Photo) {
    const base = serverDetailRef.current
    if (!user || !base) return
    setDeletingPhoto(true)
    try {
      const result = await executeOrQueueJson<void>({ userId: user.id, label: `Удаление фото ${base.number}`, method: 'DELETE', path: `/work-orders/${id}/photos/${photo.id}` })
      if (result.queued) {
        await sync.refreshQueue()
        notifyQueued('Удаление фото сохранено на устройстве, отправится при подключении')
      } else {
        const current = serverDetailRef.current ?? base
        adoptServerDetail(recountDetail({ ...current, photos: current.photos.filter((candidate) => candidate.id !== photo.id) }))
        toast.show('Фото удалено', 'success')
      }
      setPhotoToDelete(null)
    } catch (error) {
      toast.show(errorMessage(error), 'error')
    } finally {
      setDeletingPhoto(false)
    }
  }

  async function createDefect(body: DefectBody, item: ChecklistResult | null) {
    const base = serverDetailRef.current
    if (!user || !base) return
    const result = await executeOrQueueJson<Defect>({
      userId: user.id,
      label: `Дефект по ${base.number}${item ? `: пункт ${item.position}` : ''}`,
      method: 'POST',
      path: `/work-orders/${id}/defects`,
      body,
    })
    if (result.queued) {
      await sync.refreshQueue()
      notifyQueued('Дефект сохранён на устройстве, отправится при подключении')
    } else if (result.data && typeof result.data === 'object' && 'number' in result.data) {
      const defect = result.data
      const current = serverDetailRef.current ?? base
      adoptServerDetail(recountDetail({ ...current, defects: [...current.defects.filter((candidate) => candidate.id !== defect.id), defect] }))
      toast.show(`Дефект ${defect.number} создан`, 'success')
      void resource.refresh()
    }
  }

  async function addMeasurement(body: MeasurementBody) {
    const base = serverDetailRef.current
    if (!user || !base) return
    const result = await executeOrQueueJson<ChecklistResult>({ userId: user.id, label: `Измерение по ${base.number}: ${body.title}`, method: 'POST', path: `/work-orders/${id}/measurements`, body })
    if (result.queued) {
      await sync.refreshQueue()
      notifyQueued('Измерение сохранено на устройстве, отправится при подключении')
    } else if (result.data && typeof result.data === 'object' && 'resultType' in result.data) {
      const measurement = result.data
      const current = serverDetailRef.current ?? base
      adoptServerDetail(recountDetail({ ...current, checklist: [...current.checklist.filter((candidate) => candidate.id !== measurement.id), measurement] }))
      toast.show('Измерение добавлено', 'success')
      void resource.refresh()
    }
  }

  async function completeWork(comment: string): Promise<CompletionIssue[] | null> {
    const base = serverDetailRef.current
    if (!user || !base) return null
    const trimmed = comment.trim()
    try {
      const result = await executeOrQueueJson<WorkOrderDetail>({ userId: user.id, label: `Завершение работы ${base.number}`, method: 'POST', path: `/work-orders/${id}/complete`, body: trimmed ? { comment: trimmed } : {} })
      if (result.queued) {
        await sync.refreshQueue()
        toast.show('Завершение сохранено на устройстве, отправится при подключении', 'info')
      } else {
        if (isWorkOrderDetail(result.data) && user) void putCached(user.id, cacheKey, { data: result.data }).catch(() => undefined)
        toast.show(`Работа ${base.number} завершена`, 'success')
      }
      navigate('/work-orders')
      return null
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CHECKLIST_INCOMPLETE') {
        const details = error.details as { issues?: CompletionIssue[] } | undefined
        const serverIssues = Array.isArray(details?.issues) && details.issues.length ? details.issues : [{ code: 'CHECKLIST_INCOMPLETE', message: error.message }]
        void resource.refresh()
        return serverIssues
      }
      throw error
    }
  }

  if (resource.loading && !detail) return <LoadingState label="Открываем задание" />
  if (resource.error && !detail) return <div className="page-stack"><WorkOrderBackLink /><ErrorState error={resource.error} onRetry={() => void resource.refresh()} /></div>
  if (!detail) return <EmptyState icon={<ClipboardList />} title="Задание не найдено" description="Возможно, оно ещё не сохранено на этом устройстве. Откройте его при подключении к сети." action={<Link className="button button--secondary" to="/work-orders">К списку заданий</Link>} />

  const equipmentCode = detail.equipment?.externalKey || detail.equipment?.code
  const pendingDeviceState = queued.started && canStartWorkOrder(serverDetail?.status) ? 'start' : queued.completed ? 'complete' : queued.claimed && !serverDetail?.assignee ? 'claim' : null
  const photosByResult = new Map<string, Photo[]>()
  for (const photo of detail.photos) {
    if (photo.checklistResultId) photosByResult.set(photo.checklistResultId, [...(photosByResult.get(photo.checklistResultId) ?? []), photo])
  }
  const generalPhotos = detail.photos.filter((photo) => !photo.checklistResultId)
  const generalPending = queued.photos.filter((photo) => !photo.resultId)
  const defectItem = defectTarget ? defectTarget.item : null
  const defectPhotos = defectItem ? photosByResult.get(defectItem.id) ?? [] : detail.photos

  function openPhoto(photoId: string) {
    setPhotoViewer({ workOrderId: id, initialId: photoId, photos: detail!.photos.map((photo) => ({ id: photo.id, src: photo.url, caption: captionFor(photo, detail!) })) })
  }

  function openPendingPhoto(photo: PendingPhoto) {
    if (!photo.blob || typeof URL.createObjectURL !== 'function') return
    // Просмотр сохраняет собственную ссылку: миниатюра исчезнет после отправки из офлайн-очереди.
    const url = URL.createObjectURL(photo.blob)
    setPhotoViewer({ workOrderId: id, initialId: photo.id, photos: [{ id: photo.id, src: url, caption: photo.caption || photo.fileName, local: true }] })
  }

  return (
    <div className="page-stack work-detail wo-detail">
      <WorkOrderBackLink />
      {resource.source === 'cache' && resource.fetchedAt && <CachedNotice fetchedAt={resource.fetchedAt} stale={resource.stale} />}

      <section className="work-detail__hero wo-hero">
        <div className="wo-hero__body">
          <div className="wo-hero__status">
            <Badge tone={workOrderStatusTone(detail.status)}>{workOrderStatusLabel(detail.status)}</Badge>
            <span className="wo-hero__number">{detail.number}</span>
            {detail.priority === 'HIGH' && <span className="wo-priority"><AlertTriangle size={14} aria-hidden="true" />Высокий приоритет</span>}
            {poolMember && <Badge tone="info">Свободное задание</Badge>}
            {pendingDeviceState && <span className="wo-pending wo-pending--queued"><CloudOff size={13} aria-hidden="true" />{pendingDeviceState === 'start' ? 'Начало ожидает отправки' : pendingDeviceState === 'claim' ? 'Взятие в работу ожидает отправки' : 'Завершение ожидает отправки'}</span>}
          </div>
          <h2>{displayTitle(detail)}</h2>
          <p>
            <Link className="wo-hero__equipment" to={`/equipment/${detail.equipment.id}`}><Boxes size={16} aria-hidden="true" />{detail.equipment.name}{equipmentCode ? ` · ${equipmentCode}` : ''}</Link>
            {detail.object?.name && <span> · {detail.object.name}</span>}
          </p>
          <div className="work-detail__meta wo-hero__meta">
            <span><CalendarDays size={14} aria-hidden="true" />Плановая дата: {formatDate(detail.plannedAt)}</span>
            <span>Начало работы: {detail.startedAt ? formatDate(detail.startedAt, true) : 'ещё не начато'}</span>
            <span><Timer size={14} aria-hidden="true" />{formatDuration(detail.durationMinutes)}</span>
            {detail.program && <span><ClipboardList size={14} aria-hidden="true" />{detail.program.name} · версия {detail.program.version}</span>}
            {detail.maintenanceType && <span>{detail.maintenanceType.name}</span>}
            {detail.equipment.system && <span><Boxes size={14} aria-hidden="true" />{systemCode(detail) ? `${systemCode(detail)} · ` : ''}{detail.equipment.system.name}</span>}
            <span><UserRound size={14} aria-hidden="true" />{canAssign
              ? <AssigneeSelect workOrderId={detail.id} objectId={detail.object?.id} value={detail.assignee?.id ?? ''} label={detail.assignee?.fullName ?? null} onChanged={adoptServerDetail} />
              : detail.assignee?.fullName || (poolMember ? 'Свободно — можно взять в работу' : 'Исполнитель не назначен')}</span>
            {(detail.group || planner) && <span><Users size={14} aria-hidden="true" />{planner
              ? <GroupSelect workOrderId={detail.id} objectId={detail.object?.id} value={detail.group?.id ?? ''} label={detail.group?.name ?? null} disabled={!canEditGroup} onChanged={(group) => adoptServerDetail({ ...detail, group })} />
              : `Группа: ${detail.group?.name}`}</span>}
          </div>
          {detail.checklistCount > 0 && (
            <div className="wo-hero__progress">
              <span className="mywork-progress" role="img" aria-label={`Чек-лист: ${detail.answeredCount} из ${detail.checklistCount}`}><i style={{ width: `${Math.round((detail.answeredCount / detail.checklistCount) * 100)}%` }} /></span>
              <span>Чек-лист: {detail.answeredCount} из {detail.checklistCount}</span>
            </div>
          )}
        </div>
        <div className="wo-hero__actions">
          {poolMember && !planner && <Button type="button" variant="secondary" onClick={() => void claimWork()} loading={claiming} disabled={starting}><Hand size={17} aria-hidden="true" />Взять в работу</Button>}
          {startable && <Button type="button" onClick={() => void startWork()} loading={starting} disabled={claiming}><Play size={17} aria-hidden="true" />{detail.status === 'REJECTED' ? 'Начать доработку' : 'Начать работу'}</Button>}
          {editable && <Button type="button" onClick={() => setCompleteOpen(true)} disabled={!completable}><CheckCircle2 size={17} aria-hidden="true" />Завершить работу</Button>}
          {editable && photoMissing && <span className="wo-hero__locked" role="status"><ImagePlus size={16} aria-hidden="true" />Для завершения работы добавьте хотя бы одну фотографию.</span>}
          {editable && completable && <span className="wo-hero__locked" role="status"><Info size={16} aria-hidden="true" />Все обязательные данные заполнены. Нажмите «Завершить работу» и подтвердите завершение — после этого статус изменится на «Выполнено».</span>}
          {releasable && <Button type="button" variant="quiet" onClick={() => setReleaseOpen(true)} loading={releasing} disabled={starting || claiming}><Undo2 size={17} aria-hidden="true" />Вернуть задание</Button>}
          {foreign && <Button type="button" variant="secondary" onClick={() => setTakeOverOpen(true)} loading={takingOver}><Hand size={17} aria-hidden="true" />Продолжить за коллегу</Button>}
          {poolMember && !planner && <span className="wo-hero__locked"><Info size={16} aria-hidden="true" />Свободное задание объекта: «Взять в работу» закрепит его за вами, «Начать работу» — закрепит и начнёт сразу</span>}
          {!canExecute && !finished && <span className="wo-hero__locked"><Info size={16} aria-hidden="true" />{detail.assignee ? `Выполняет ${detail.assignee.fullName} — только просмотр` : 'Назначено другому исполнителю — только просмотр'}{foreign ? '. Если коллега не может продолжить, нажмите «Продолжить за коллегу»' : ''}</span>}
        </div>
      </section>

      {detail.originalPlannedAt && detail.transferReason && <div className="form-message form-message--warning wo-notice" role="status"><CalendarDays size={18} aria-hidden="true" /><span>Перенесено с {formatDate(detail.originalPlannedAt)}{detail.transferredAt ? ` (${formatDate(detail.transferredAt)})` : ''}: {detail.transferReason}</span></div>}
      {detail.status === 'CANCELLED' && <div className="form-message form-message--error wo-notice" role="status"><AlertTriangle size={18} aria-hidden="true" /><span>Работа отменена{detail.cancelReason ? `: ${detail.cancelReason}` : ''}</span></div>}
      {detail.status === 'REJECTED' && <div className="form-message form-message--error wo-notice" role="status"><AlertTriangle size={18} aria-hidden="true" /><span><strong>Возвращено на доработку{detail.reviewedBy ? ` (${detail.reviewedBy.fullName})` : ''}{detail.reviewedAt ? `, ${formatDate(detail.reviewedAt, true)}` : ''}.</strong> {detail.reviewComment || 'Проверяющий не оставил комментарий.'} Начните доработку, исправьте замечания и завершите работу снова.</span></div>}
      {detail.status === 'COMPLETED' && (
        <div className={`form-message ${detail.resultStatus === 'DEVIATIONS' ? 'form-message--warning' : 'form-message--success'} wo-notice`} role="status">
          <CheckCircle2 size={18} aria-hidden="true" />
          <span>
            <strong>{detail.resultStatus === 'DEVIATIONS' ? 'Выполнено с отклонениями' : 'Выполнено без отклонений'}</strong>
            {detail.completedAt ? ` · ${formatDate(detail.completedAt, true)}` : ''}{detail.completedBy ? ` · ${detail.completedBy.fullName}` : ''}
            {detail.reviewedAt ? ` · принято ${formatDate(detail.reviewedAt, true)}` : ' · ожидает проверки'}
            {detail.executorComment && <><br />Комментарий исполнителя: {detail.executorComment}</>}
          </span>
        </div>
      )}
      {queued.attention > 0 && <div className="form-message form-message--error wo-notice" role="alert"><CloudOff size={18} aria-hidden="true" /><span>Часть изменений по этому заданию не удалось отправить. <Link className="text-link" to="/sync">Открыть сохранённые изменения</Link></span></div>}
      {detail.description && <section className="surface wo-description"><h3>Описание работы</h3><p>{detail.description}</p></section>}


      <section className="surface wo-checklist">
        <div className="section-heading">
          <div><h3>Чек-лист</h3><p>{detail.checklistCount ? `${detail.answeredCount} из ${detail.checklistCount} пунктов заполнено` : programMissing ? 'Программа ТО ещё не назначена' : 'Программа ТО не содержит пунктов'}{saving > 0 ? ' · сохраняем…' : ''}</p></div>
          {editable && !programMissing && <Button type="button" variant="secondary" onClick={() => setMeasurementOpen(true)}><Ruler size={17} aria-hidden="true" />Добавить измерение</Button>}
        </div>
        {programMissing && <div className="form-message form-message--error wo-notice" role="alert"><AlertTriangle size={18} aria-hidden="true" /><span>Для этого оборудования инженер ещё не назначил программу ТО, поэтому чек-листа нет и завершить работу пока нельзя. Сообщите менеджеру; измерения вне программы здесь не нужны — замечания можно написать в комментарии при завершении.</span></div>}
        {!editable && !finished && canExecute && <p className="checklist-photo-note wo-checklist__hint"><Info size={16} aria-hidden="true" />Нажмите «Начать работу», чтобы заполнять чек-лист{detail.status === 'REJECTED' ? '. Предыдущие ответы сохранены' : ''}.</p>}
        {detail.checklist.length === 0
          ? <p className="quiet-placeholder">Пунктов чек-листа нет. Добавьте измерение, если оно нужно для отчёта.</p>
          : <ol className="checklist wo-checklist__list">
            {[...detail.checklist].sort((a, b) => a.position - b.position).map((item) => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                editable={editable}
                pending={drafts.has(item.id) ? 'draft' : queued.resultIds.has(item.id) || isPendingId(item.id) ? 'queued' : null}
                photos={photosByResult.get(item.id) ?? []}
                pendingPhotos={queued.photos.filter((photo) => photo.resultId === item.id)}
                defect={defectForResult(detail, item.id)}
                pendingDefect={pendingDefectForResult(queued.defects, item.id)}
                issues={issuesByResult.get(item.id) ?? []}
                canCreateDefect={canCreateDefect && !isPendingId(item.id)}
                canDeletePhotos={editable}
                onChange={(patch) => updateItem(item, patch)}
                onAddPhoto={() => openPhotoPicker(item.id)}
                onCreateDefect={() => setDefectTarget({ item })}
                onDeletePhoto={(photo) => setPhotoToDelete(photo)}
                onViewPhoto={openPhoto}
                onViewPendingPhoto={openPendingPhoto}
              />
            ))}
          </ol>}
      </section>

      <section className="surface wo-photos">
        <div className="section-heading">
          <div><h3>Фотографии</h3><p>{detail.photos.length + queued.photos.length ? `${detail.photos.length + queued.photos.length} фото${queued.photos.length ? `, ${queued.photos.length} ожидает отправки` : ''}` : finished ? 'Фотографий пока нет' : 'Для завершения нужна хотя бы одна фотография'}</p></div>
          {editable && <Button type="button" variant="secondary" onClick={() => openPhotoPicker(null)} loading={uploading}><Camera size={17} aria-hidden="true" />Добавить фото</Button>}
        </div>
        {detail.photos.length === 0 && queued.photos.length === 0
          ? <p className="photo-empty"><ImagePlus size={18} aria-hidden="true" />{editable ? 'Снимите общий вид оборудования или приложите фото к конкретному пункту чек-листа.' : finished ? 'Работа завершена: фотографии больше не добавляются. Если нужно дополнить, попросите менеджера вернуть задание на доработку.' : !canExecute ? `Фото добавляет исполнитель, за которым закреплено задание${detail.assignee ? ` (${detail.assignee.fullName})` : ''}.` : startable ? 'Нажмите «Начать работу» — после этого можно добавлять фото.' : 'К заданию не приложено фотографий.'}</p>
          : <div className="photo-list wo-photo-list">
            {detail.photos.map((photo) => <PhotoCard photo={photo} key={photo.id} caption={captionFor(photo, detail)} onView={() => openPhoto(photo.id)} onDelete={editable && !photo.defectId ? () => setPhotoToDelete(photo) : undefined} />)}
            {queued.photos.map((photo) => <PendingPhotoCard photo={photo} key={photo.id} onView={() => openPendingPhoto(photo)} />)}
          </div>}
        {(generalPhotos.length > 0 || generalPending.length > 0) && detail.checklist.length > 0 && <p className="field-help">Общих фото задания: {generalPhotos.length + generalPending.length}</p>}
      </section>

      <section className="surface wo-defects">
        <div className="section-heading">
          <div><h3>Дефекты</h3><p>{detail.defects.length + queued.defects.length ? `${detail.defects.length + queued.defects.length} по этому заданию` : 'Дефектов по заданию нет'}</p></div>
          {canCreateDefect && <Button type="button" variant="secondary" onClick={() => setDefectTarget({ item: null })}><Wrench size={17} aria-hidden="true" />Создать дефект</Button>}
        </div>
        {detail.defects.length === 0 && queued.defects.length === 0
          ? <p className="quiet-placeholder">{editable ? 'Если при осмотре найдена неисправность, зарегистрируйте дефект — из пункта «Не норма» или общей кнопкой.' : 'Дефекты не регистрировались.'}</p>
          : <div className="wo-defect-list">
            {detail.defects.map((defect) => <DefectCard defect={defect} key={defect.id} />)}
            {queued.defects.map((defect) => <PendingDefectCard defect={defect} key={defect.id} />)}
          </div>}
      </section>

      {editable && (
        <section className="wo-actionbar" aria-label="Действия по заданию">
          <div className={`wo-actionbar__status${completable ? ' wo-actionbar__status--ready' : ''}`}>
            {completable ? <><CheckCircle2 size={18} aria-hidden="true" /><span>Готово к завершению</span></> : <><AlertTriangle size={18} aria-hidden="true" /><span>{photoMissing ? 'Добавьте хотя бы одно фото' : `Осталось пунктов: ${issues.length}`}</span></>}
          </div>
          <div className="wo-actionbar__buttons">
            <Button type="button" variant="secondary" onClick={() => openPhotoPicker(null)} loading={uploading}><Camera size={17} aria-hidden="true" />Фото</Button>
            <Button type="button" onClick={() => setCompleteOpen(true)} disabled={!completable}><CheckCircle2 size={17} aria-hidden="true" />Завершить работу</Button>
          </div>
        </section>
      )}

      <input name="work-order-photo" ref={fileInputRef} className="sr-only" type="file" accept="image/*" capture="environment" aria-label="Выбрать фотографию" tabIndex={-1} onChange={onPhotoChosen} />

      {completeOpen && detail && (
        <CompleteModal
          workOrder={detail}
          onClose={() => setCompleteOpen(false)}
          onSubmit={completeWork}
        />
      )}
      {measurementOpen && (
        <MeasurementModal
          onClose={() => setMeasurementOpen(false)}
          onSubmit={async (body) => { await addMeasurement(body); setMeasurementOpen(false) }}
        />
      )}
      {defectTarget && (
        <DefectModal
          workOrder={detail}
          item={defectItem}
          photos={defectPhotos}
          planner={planner}
          onClose={() => setDefectTarget(null)}
          onSubmit={async (body) => { await createDefect(body, defectItem); setDefectTarget(null) }}
        />
      )}
      <ConfirmDialog
        open={releaseOpen}
        title="Вернуть задание в свободные?"
        description="Задание перестанет быть закреплённым за вами, отметка о начале снимется. Его сможет взять любой исполнитель вашей группы."
        confirmLabel="Вернуть задание"
        loading={releasing}
        onClose={() => setReleaseOpen(false)}
        onConfirm={() => void releaseWork()}
      />
      <ConfirmDialog
        open={takeOverOpen}
        title="Продолжить задание за коллегу?"
        description={detail?.assignee ? `Задание закрепится за вами вместо ${detail.assignee.fullName}. Ответы и фотографии коллеги сохранятся, вы продолжите с текущего места.` : 'Задание закрепится за вами.'}
        confirmLabel="Продолжить за коллегу"
        loading={takingOver}
        onClose={() => setTakeOverOpen(false)}
        onConfirm={() => void takeOverWork()}
      />
      {photoViewer?.workOrderId === id && <PhotoViewer photos={photoViewer.photos} initialId={photoViewer.initialId} onClose={() => setPhotoViewer(null)} />}
      <ConfirmDialog
        open={Boolean(photoToDelete)}
        title="Удалить фотографию?"
        description={photoToDelete ? `${photoToDelete.caption || photoToDelete.file.name} будет удалена из задания.` : ''}
        confirmLabel="Удалить фото"
        tone="danger"
        loading={deletingPhoto}
        onClose={() => setPhotoToDelete(null)}
        onConfirm={() => { if (photoToDelete) void deletePhoto(photoToDelete) }}
      />
    </div>
  )
}

/** Исполнитель задания: менеджер или администратор выбирает сотрудника, изменение сохраняется сразу. */
function AssigneeSelect({ workOrderId, objectId, value, label, onChanged }: { workOrderId: string; objectId?: string; value: string; label: string | null; onChanged(detail: WorkOrderDetail): void }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const assignees = useOfflineQuery<User[]>('work-orders:assignees', '/work-orders/assignees', { maxAgeMs: 10 * 60_000 })
  const options = assignees.data ?? []

  async function change(event: ChangeEvent<HTMLSelectElement>) {
    const next = event.target.value
    setSaving(true)
    try {
      const detail = await apiRequest<WorkOrderDetail>(`/work-orders/${workOrderId}`, { method: 'PATCH', body: { assigneeId: next || null } })
      onChanged(detail)
      toast.show(detail.assignee ? `Исполнитель назначен: ${detail.assignee.fullName}.` : 'Исполнитель снят с задания.', 'success')
    } catch (caught) {
      toast.show(errorMessage(caught), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (assignees.error && options.length === 0) return <>{label || 'Исполнитель не назначен'}</>
  return <select name="workOrderAssigneeId" aria-label="Исполнитель задания" className="wo-group-select" value={value} disabled={saving || assignees.loading} onChange={(event) => void change(event)}>
    <option value="">Без исполнителя</option>
    <AssigneeOptions people={options} objectId={objectId} />
  </select>
}

/** Группа задания для менеджера: выбор из групп объекта, сохраняется сразу (PATCH groupId). */
function GroupSelect({ workOrderId, objectId, value, label, disabled, onChanged }: { workOrderId: string; objectId?: string; value: string; label: string | null; disabled: boolean; onChanged(group: ExecutorGroupRef | null): void }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const groups = useOfflineQuery<ExecutorGroupRef[]>(`groups:${objectId || 'all'}`, objectId ? `/groups?objectId=${encodeURIComponent(objectId)}` : '/groups', { maxAgeMs: 10 * 60_000 })
  const options = groups.data ?? []
  if (disabled || options.length === 0) return <>{label ? `Группа: ${label}` : 'Группа не назначена'}</>

  async function change(event: ChangeEvent<HTMLSelectElement>) {
    const next = event.target.value
    setSaving(true)
    try {
      const detail = await apiRequest<WorkOrderDetail>(`/work-orders/${workOrderId}`, { method: 'PATCH', body: { groupId: next || null } })
      onChanged(detail.group ?? null)
      toast.show(detail.group ? `Задание передано группе «${detail.group.name}».` : 'Группа с задания снята.', 'success')
    } catch (caught) {
      toast.show(errorMessage(caught), 'error')
    } finally {
      setSaving(false)
    }
  }

  return <select name="workOrderGroupId" aria-label="Группа исполнителей" className="wo-group-select" value={value} disabled={saving} onChange={(event) => void change(event)}>
    <option value="">Без группы</option>
    {options.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
  </select>
}

function captionFor(photo: Photo, detail: WorkOrderDetail): string {
  if (photo.caption) return photo.caption
  const item = photo.checklistResultId ? detail.checklist.find((candidate) => candidate.id === photo.checklistResultId) : null
  return item ? `Пункт ${item.position}: ${item.title}` : 'Общее фото задания'
}

interface ChecklistItemRowProps {
  item: ChecklistResult
  editable: boolean
  pending: 'draft' | 'queued' | null
  photos: Photo[]
  pendingPhotos: PendingPhoto[]
  defect: Defect | null
  pendingDefect: PendingDefect | null
  issues: CompletionIssue[]
  canCreateDefect: boolean
  canDeletePhotos: boolean
  onChange(patch: Partial<ChecklistResultInput>): void
  onAddPhoto(): void
  onCreateDefect(): void
  onDeletePhoto(photo: Photo): void
  onViewPhoto(photoId: string): void
  onViewPendingPhoto(photo: PendingPhoto): void
}

function ChecklistItemRow({ item, editable, pending, photos, pendingPhotos, defect, pendingDefect, issues, canCreateDefect, canDeletePhotos, onChange, onAddPhoto, onCreateDefect, onDeletePhoto, onViewPhoto, onViewPendingPhoto }: ChecklistItemRowProps) {
  const [commentOpen, setCommentOpen] = useState(false)
  const type = item.resultType
  const skipped = item.answer === 'SKIPPED'
  const deviation = item.answer === 'NOT_NORMAL'
  const problem = deviation || item.outOfRange
  const pendingItem = isPendingId(item.id)
  const controlsEnabled = editable && !pendingItem
  const hasComment = Boolean((item.comment ?? '').trim())
  const commentRequired = deviation
  const showComment = controlsEnabled ? deviation || skipped || hasComment || commentOpen : hasComment
  const hasPhotos = photos.length > 0 || pendingPhotos.length > 0
  const photoNeeded = (!skipped || item.required) && (item.photoRequired || type === 'PHOTO' || deviation)
  const hint = isNumericType(type) ? normHint(item) : null
  const numericInvalid = isNumericType(type) && !normalizeDecimalInput(item.valueNumeric ?? null).valid
  const labels = choiceLabels(type)
  const valueLabel = resultValueLabel(item)
  const answerTone = deviation ? 'danger' : skipped ? 'neutral' : 'success'
  const answerText = isChoiceType(type) || skipped || deviation ? answerLabel(item.answer, type) : 'Заполнено'

  return (
    <li className={`checklist-row wo-item${problem ? ' checklist-row--problem wo-item--problem' : ''}${item.answered ? ' wo-item--answered' : ''}`} id={`wo-item-${item.id}`}>
      <span className="checklist-row__number" aria-hidden="true">{item.position}</span>
      <div className="checklist-row__content wo-item__content">
        <div className="wo-item__head">
          <div className="wo-item__title">
            <strong>{item.title}{item.required && <em>обязательно</em>}</strong>
            {item.description && <p className="wo-item__description">{item.description}</p>}
          </div>
          <div className="wo-item__tags">
            <span className="wo-item__type">{item.adHoc ? 'Измерение вне программы' : resultTypeLabel(type)}</span>
            {item.answered && <Badge tone={answerTone}>{answerText}</Badge>}
            {pending && <span className={`wo-pending wo-pending--${pending}`}><CloudOff size={13} aria-hidden="true" />{pending === 'draft' ? 'Сохраняем…' : 'Ожидает отправки'}</span>}
          </div>
        </div>

        {controlsEnabled ? (
          <div className="wo-item__controls">
            {isChoiceType(type) && (
              <div className="checklist-row__choices" role="group" aria-label={item.title}>
                <button type="button" className={`choice choice--normal${item.answer === 'NORMAL' ? ' choice--selected' : ''}`} aria-pressed={item.answer === 'NORMAL'} onClick={() => onChange({ answer: item.answer === 'NORMAL' ? null : 'NORMAL' })}><CheckCircle2 size={16} aria-hidden="true" />{labels.normal}</button>
                <button type="button" className={`choice choice--problem${deviation ? ' choice--selected' : ''}`} aria-pressed={deviation} onClick={() => onChange({ answer: deviation ? null : 'NOT_NORMAL' })}><AlertTriangle size={16} aria-hidden="true" />{labels.problem}</button>
                {!item.required && <button type="button" className={`choice wo-choice-skip${skipped ? ' choice--selected' : ''}`} aria-pressed={skipped} onClick={() => onChange({ answer: skipped ? null : 'SKIPPED' })}>Пропустить</button>}
              </div>
            )}
            {isNumericType(type) && (
              <>
                <label className={`inline-field wo-numeric${item.outOfRange ? ' inline-field--problem' : ''}`}>
                  <span>Значение{hint && <small>{hint}</small>}</span>
                  <span className="wo-numeric__control">
                    <input name={`checklist-${item.id}-value`} type="text" inputMode="decimal" autoComplete="off" aria-label={`Значение: ${item.title}`} value={displayDecimal(item.valueNumeric)} placeholder={item.unit ? `0 ${item.unit}` : '0'} disabled={skipped} onChange={(event) => onChange({ value: event.target.value })} />
                    {item.unit && <span className="wo-numeric__unit">{item.unit}</span>}
                  </span>
                </label>
                {numericInvalid && <p className="validation-hint">Введите число, например 2,5</p>}
                {item.outOfRange && !numericInvalid && <p className="validation-hint">Значение вне нормы — результат «Не норма». Опишите отклонение и добавьте фото.</p>}
              </>
            )}
            {type === 'TEXT' && (
              <label className="field wo-field"><span>Ответ</span><textarea name={`checklist-${item.id}-value`} aria-label={`Ответ: ${item.title}`} rows={3} value={item.valueText ?? ''} disabled={skipped} placeholder="Опишите результат" onChange={(event) => onChange({ value: event.target.value })} /></label>
            )}
            {type === 'DATE' && (
              <label className="inline-field wo-date"><span>Дата</span><input name={`checklist-${item.id}-value`} type="date" aria-label={`Дата: ${item.title}`} value={item.valueText ?? ''} disabled={skipped} onChange={(event) => onChange({ value: event.target.value })} /></label>
            )}
            {type === 'SELECT' && (
              <label className="inline-field wo-select"><span>Вариант</span><select name={`checklist-${item.id}-value`} aria-label={`Вариант: ${item.title}`} value={item.valueText ?? ''} disabled={skipped} onChange={(event) => onChange({ value: event.target.value })}><option value="">Не выбрано</option>{(item.options ?? []).map((option) => <option value={option} key={option}>{option}</option>)}</select></label>
            )}
            {type === 'MULTISELECT' && (
              <fieldset className="wo-options" disabled={skipped}>
                <legend className="sr-only">{item.title}</legend>
                <div className="checklist-options">
                  {(item.options ?? []).map((option) => {
                    const values = multiSelectValues(item.valueText)
                    const checked = values.includes(option)
                    return <label key={option}><input name={`checklist-${item.id}-option-${option}`} type="checkbox" checked={checked} onChange={() => onChange({ value: checked ? values.filter((value) => value !== option) : [...values, option] })} />{option}</label>
                  })}
                </div>
              </fieldset>
            )}
            {!isChoiceType(type) && (
              <div className="checklist-row__choices wo-item__secondary">
                {(!item.required || skipped) && <button type="button" className={`choice wo-choice-skip${skipped ? ' choice--selected' : ''}`} aria-pressed={skipped} onClick={() => onChange({ answer: skipped ? null : 'SKIPPED' })}>{item.required ? 'Заполнить пункт' : skipped ? 'Пропущено — вернуть' : 'Пропустить пункт'}</button>}
                {!showComment && <button type="button" className="choice" onClick={() => setCommentOpen(true)}><MessageSquareText size={15} aria-hidden="true" />Комментарий</button>}
              </div>
            )}
            {isChoiceType(type) && !showComment && (
              <div className="wo-item__secondary"><button type="button" className="choice" onClick={() => setCommentOpen(true)}><MessageSquareText size={15} aria-hidden="true" />Добавить комментарий</button></div>
            )}
            {showComment && (
              <label className={`field wo-comment${commentRequired && !hasComment ? ' wo-comment--required' : ''}`}>
                <span>Комментарий<em>{commentRequired ? 'обязательно' : 'необязательно'}</em></span>
                <textarea name={`checklist-${item.id}-comment`} aria-label={`Комментарий: ${item.title}`} rows={2} value={item.comment ?? ''} placeholder={deviation ? 'Опишите отклонение' : skipped ? 'Почему пункт пропущен' : 'Комментарий к пункту'} onChange={(event) => onChange({ comment: event.target.value })} />
              </label>
            )}
            {issues.length > 0 && (
              <ul className="wo-item__hints">
                {issues.map((issue) => <li key={issue.code}><AlertTriangle size={14} aria-hidden="true" />{issueHint(issue.code)}</li>)}
              </ul>
            )}
          </div>
        ) : (
          <div className="wo-item__readonly">
            {isChoiceType(type)
              ? <span className={`wo-answer wo-answer--${item.answered ? answerTone : 'empty'}`}>{answerLabel(item.answer, type)}</span>
              : valueLabel
                ? <span className="wo-value">{valueLabel}{item.outOfRange && <em> · вне нормы</em>}</span>
                : <span className="wo-value wo-value--empty">{skipped ? 'Пропущено' : 'Нет ответа'}</span>}
            {hint && <small>{hint}</small>}
            {hasComment && <p className="wo-item__comment"><MessageSquareText size={15} aria-hidden="true" />{item.comment}</p>}
            {item.answeredAt && <small>Отвечено {formatDate(item.answeredAt, true)}{item.answeredBy ? ` · ${item.answeredBy.fullName}` : ''}</small>}
          </div>
        )}

        {(hasPhotos || controlsEnabled) && (
          <div className="wo-item__photos">
            {hasPhotos && (
              <div className="photo-list wo-photo-list">
                {photos.map((photo) => <PhotoCard photo={photo} key={photo.id} caption={photo.caption || `Пункт ${item.position}`} onView={() => onViewPhoto(photo.id)} onDelete={canDeletePhotos && !photo.defectId ? () => onDeletePhoto(photo) : undefined} />)}
                {pendingPhotos.map((photo) => <PendingPhotoCard photo={photo} key={photo.id} onView={() => onViewPendingPhoto(photo)} />)}
              </div>
            )}
            {controlsEnabled && (
              <div className="wo-item__photo-actions">
                <Button type="button" variant="secondary" onClick={onAddPhoto}><Camera size={17} aria-hidden="true" />{type === 'PHOTO' ? 'Добавить фото' : 'Добавить фото к пункту'}</Button>
                {photoNeeded && !hasPhotos && <span className="checklist-photo-note"><ImagePlus size={16} aria-hidden="true" />{deviation ? 'Для результата «Не норма» нужна фотография отклонения' : 'Для этого пункта нужна фотография'}</span>}
              </div>
            )}
          </div>
        )}

        {defect
          ? <div className="wo-item__defect"><Wrench size={16} aria-hidden="true" /><span>Дефект <Link to={`/defects/${defect.id}`}>{defect.number}</Link> · {defectPriorityLabel(defect.priority)} · {defectStatusLabel(defect.status)}</span></div>
          : pendingDefect
            ? <div className="wo-item__defect wo-item__defect--pending"><CloudOff size={16} aria-hidden="true" /><span>Дефект «{pendingDefect.title}» ожидает отправки</span></div>
            : deviation && canCreateDefect
              ? <div className="wo-item__defect-cta">
                <Button type="button" variant={item.defectRule === 'REQUIRE' ? 'danger' : 'primary'} onClick={onCreateDefect}><Wrench size={17} aria-hidden="true" />Создать дефект</Button>
                <span className="wo-item__defect-note">{item.defectRule === 'REQUIRE' ? 'По правилу программы дефект обязателен для завершения работы' : 'Зарегистрируйте неисправность, чтобы её устранили'}</span>
              </div>
              : null}
      </div>
    </li>
  )
}

function PhotoCard({ photo, caption, onView, onDelete }: { photo: Photo; caption: string; onView(): void; onDelete?: () => void }) {
  return (
    <figure className="photo-card wo-photo">
      <span className="wo-photo__preview">
        <AuthImage src={photo.url} alt={caption} loading="lazy" />
        <button type="button" className="wo-photo__open" onClick={onView} aria-label={`Открыть фото: ${caption}`}><span><ZoomIn size={18} aria-hidden="true" /></span></button>
      </span>
      <figcaption title={caption}>{caption}</figcaption>
      {onDelete && <button type="button" className="wo-photo__delete" onClick={onDelete} aria-label={`Удалить фото: ${caption}`}><Trash2 size={15} aria-hidden="true" /></button>}
    </figure>
  )
}

function PendingPhotoCard({ photo, onView }: { photo: PendingPhoto; onView(): void }) {
  const [url] = useState(() => (photo.blob && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(photo.blob) : null))
  useEffect(() => () => {
    if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url)
  }, [url])
  const label = photo.state === 'retry' ? 'Повторим отправку' : photo.state === 'syncing' ? 'Отправляется' : photo.state === 'auth-required' ? 'Нужен повторный вход' : 'Ожидает отправки'
  return (
    <figure className="photo-card wo-photo wo-photo--pending" aria-label={`${photo.fileName}: ${label}`}>
      {url ? <span className="wo-photo__preview"><img src={url} alt={photo.caption || photo.fileName} /><button type="button" className="wo-photo__open" onClick={onView} aria-label={`Открыть фото: ${photo.caption || photo.fileName}`}><span><ZoomIn size={18} aria-hidden="true" /></span></button></span> : <div><Camera size={22} aria-hidden="true" /></div>}
      <figcaption><CloudOff size={12} aria-hidden="true" />{label}</figcaption>
    </figure>
  )
}

function DefectCard({ defect }: { defect: Defect }) {
  return (
    <Link className="wo-defect-card" to={`/defects/${defect.id}`}>
      <span className="wo-defect-card__icon"><Wrench size={18} aria-hidden="true" /></span>
      <span className="wo-defect-card__body">
        <strong>{defect.number} · {defect.title}</strong>
        <small>{defect.sourceResult ? `Пункт ${defect.sourceResult.position}: ${defect.sourceResult.title}` : defect.component ? `Компонент: ${defect.component.name}` : 'По заданию в целом'}{defect.assignee ? ` · ${defect.assignee.fullName}` : ''}{defect.dueDate ? ` · срок ${formatDate(defect.dueDate)}` : ''}</small>
      </span>
      <span className="wo-defect-card__badges">
        <Badge tone={defectPriorityTone(defect.priority)}>{defectPriorityLabel(defect.priority)}</Badge>
        <Badge tone={defectStatusTone(defect.status)}>{defectStatusLabel(defect.status)}</Badge>
      </span>
    </Link>
  )
}

function PendingDefectCard({ defect }: { defect: PendingDefect }) {
  return (
    <div className="wo-defect-card wo-defect-card--pending">
      <span className="wo-defect-card__icon"><CloudOff size={18} aria-hidden="true" /></span>
      <span className="wo-defect-card__body">
        <strong>{defect.title}</strong>
        <small>Ожидает отправки{defect.dueDate ? ` · срок ${formatDate(defect.dueDate)}` : ''}</small>
      </span>
      <span className="wo-defect-card__badges"><Badge tone={defectPriorityTone(defect.priority)}>{defectPriorityLabel(defect.priority)}</Badge></span>
    </div>
  )
}

function CompleteModal({ workOrder, onClose, onSubmit }: { workOrder: WorkOrderDetail; onClose(): void; onSubmit(comment: string): Promise<CompletionIssue[] | null> }) {
  const [comment, setComment] = useState(workOrder.executorComment ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [issues, setIssues] = useState<CompletionIssue[]>([])
  const deviations = workOrder.checklist.filter((item) => item.answer === 'NOT_NORMAL').length

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      const serverIssues = await onSubmit(comment)
      if (serverIssues) setIssues(serverIssues)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Завершить работу?" description={`${workOrder.number} · ${workOrder.title}`} size="small" dismissible={!submitting}>
      <form onSubmit={(event) => void submit(event)}>
        {/* Фокус остаётся на контейнере: на телефоне поле комментария не должно сразу открывать клавиатуру и закрывать кнопки. */}
        <div className="modal__body form-stack" data-autofocus tabIndex={-1}>
          <p className="wo-complete__summary">{workOrder.answeredCount} из {workOrder.checklistCount} пунктов заполнено{deviations ? ` · отклонений: ${deviations}` : ' · отклонений нет'}. После завершения чек-лист станет доступен только для просмотра.</p>
          <label className="field"><span>Комментарий исполнителя<em>необязательно</em></span><textarea name="completion-comment" rows={3} value={comment} maxLength={5000} placeholder="Что важно знать проверяющему" onChange={(event) => setComment(event.target.value)} data-autofocus /></label>
          {issues.length > 0 && (
            <div className="form-message form-message--error" role="alert">
              <AlertTriangle size={18} aria-hidden="true" />
              <div><strong>Сервер не принял завершение:</strong><ul className="wo-complete__issues">{issues.map((issue, index) => <li key={`${issue.code}-${index}`}>{issue.message}</li>)}</ul></div>
            </div>
          )}
          {error && <div className="form-message form-message--error" role="alert">{error}</div>}
        </div>
        <div className="modal__footer">
          <Button type="button" variant="quiet" onClick={onClose} disabled={submitting}>Отмена</Button>
          <Button type="submit" loading={submitting}><CheckCircle2 size={17} aria-hidden="true" />Подтвердить завершение</Button>
        </div>
      </form>
    </Modal>
  )
}

function MeasurementModal({ onClose, onSubmit }: { onClose(): void; onSubmit(body: MeasurementBody): Promise<void> }) {
  const [title, setTitle] = useState('')
  const [unit, setUnit] = useState('')
  const [minValue, setMinValue] = useState('')
  const [maxValue, setMaxValue] = useState('')
  const [value, setValue] = useState('')
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    const numbers = { minValue: normalizeDecimalInput(minValue), maxValue: normalizeDecimalInput(maxValue), value: normalizeDecimalInput(value) }
    if (!numbers.minValue.valid || !numbers.maxValue.valid || !numbers.value.valid) {
      setError('Проверьте числовые значения: допустимы цифры и до 6 знаков после запятой.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      await onSubmit({ title: title.trim(), unit: unit.trim() || null, minValue: numbers.minValue.value, maxValue: numbers.maxValue.value, value: numbers.value.value, comment: comment.trim() || null })
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Добавить измерение" description="Только числовые измерения сверх программы: величина, значение и единица. Они попадут в чек-лист и журнал измерений. Замечания без числа пишите в комментарии при завершении работы." dismissible={!submitting}>
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal__body form-stack">
          {error && <div className="form-message form-message--error" role="alert">{error}</div>}
          <label className="field"><span>Какая величина измерена</span><input name="measurement-title" value={title} maxLength={1000} required placeholder="Например, температура подшипника" onChange={(event) => setTitle(event.target.value)} data-autofocus /></label>
          <div className="form-grid form-grid--2">
            <label className="field"><span>Значение</span><input name="measurement-value" inputMode="decimal" value={value} placeholder="Число" required onChange={(event) => setValue(event.target.value)} /></label>
            <label className="field"><span>Единица<em>необязательно</em></span><input name="measurement-unit" value={unit} maxLength={50} placeholder="мм/с" onChange={(event) => setUnit(event.target.value)} /></label>
            <label className="field"><span>Норма от<em>необязательно</em></span><input name="measurement-min-value" inputMode="decimal" value={minValue} onChange={(event) => setMinValue(event.target.value)} /></label>
            <label className="field"><span>Норма до<em>необязательно</em></span><input name="measurement-max-value" inputMode="decimal" value={maxValue} onChange={(event) => setMaxValue(event.target.value)} /></label>
          </div>
          <label className="field"><span>Комментарий<em>необязательно</em></span><textarea name="measurement-comment" rows={2} value={comment} maxLength={5000} onChange={(event) => setComment(event.target.value)} /></label>
        </div>
        <div className="modal__footer">
          <Button type="button" variant="quiet" onClick={onClose} disabled={submitting}>Отмена</Button>
          <Button type="submit" loading={submitting} disabled={!title.trim() || !value.trim()}><Ruler size={17} aria-hidden="true" />Сохранить измерение</Button>
        </div>
      </form>
    </Modal>
  )
}

function DefectModal({ workOrder, item, photos, planner, onClose, onSubmit }: { workOrder: WorkOrderDetail; item: ChecklistResult | null; photos: Photo[]; planner: boolean; onClose(): void; onSubmit(body: DefectBody): Promise<void> }) {
  const [title, setTitle] = useState(item ? item.title : '')
  const [description, setDescription] = useState(item?.comment ?? '')
  const [priority, setPriority] = useState<DefectPriority>(item?.answer === 'NOT_NORMAL' ? 'HIGH' : 'NORMAL')
  const [componentId, setComponentId] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [photoIds, setPhotoIds] = useState<string[]>(() => (item ? photos.map((photo) => photo.id) : []))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const equipmentId = workOrder.equipment.id
  const components = useOfflineQuery<EquipmentComponent[]>(`equipment-components:${equipmentId}`, `/equipment/${equipmentId}/components`, { maxAgeMs: 10 * 60_000 })
  const assignees = useOfflineQuery<User[]>('work-orders:assignees', '/work-orders/assignees', { enabled: planner, maxAgeMs: 10 * 60_000 })

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      await onSubmit({
        title: title.trim(),
        resultId: item?.id,
        componentId: componentId || undefined,
        description: description.trim() || undefined,
        priority,
        assigneeId: assigneeId || undefined,
        dueDate: dueDate || undefined,
        photoIds: photoIds.length ? photoIds : undefined,
      })
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={item ? `Дефект по пункту ${item.position}` : 'Новый дефект'} description={`${workOrder.equipment.name} · ${workOrder.number}`} dismissible={!submitting}>
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal__body form-stack">
          {error && <div className="form-message form-message--error" role="alert">{error}</div>}
          {item && <p className="checklist-photo-note"><Info size={16} aria-hidden="true" />Дефект будет связан с пунктом «{item.title}»{item.answer === 'NOT_NORMAL' ? ' с результатом «Не норма»' : ''}.</p>}
          <label className="field"><span>Что обнаружено</span><input name="defect-title" value={title} maxLength={500} required placeholder="Кратко опишите неисправность" onChange={(event) => setTitle(event.target.value)} data-autofocus /></label>
          <label className="field"><span>Описание<em>необязательно</em></span><textarea name="defect-description" rows={3} value={description} maxLength={5000} placeholder="Подробности, условия обнаружения, что уже сделано" onChange={(event) => setDescription(event.target.value)} /></label>
          <div className="form-grid form-grid--2">
            <label className="field"><span>Приоритет</span><select name="defect-priority" value={priority} onChange={(event) => setPriority(event.target.value)}>{DEFECT_PRIORITIES.map((option) => <option value={option} key={option}>{defectPriorityLabel(option)}</option>)}</select></label>
            <label className="field"><span>Срок устранения<em>необязательно</em></span><input name="defect-due-date" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} /></label>
          </div>
          <label className="field">
            <span>Компонент оборудования<em>необязательно</em></span>
            <select name="defect-component-id" value={componentId} disabled={components.loading} onChange={(event) => setComponentId(event.target.value)}>
              <option value="">Оборудование целиком</option>
              {(components.data ?? []).map((component) => <option value={component.id} key={component.id}>{component.name}{component.code ? ` · ${component.code}` : ''}</option>)}
            </select>
            {components.error && <small>Список компонентов недоступен без сети — дефект можно создать без компонента.</small>}
          </label>
          {planner && (
            <label className="field">
              <span>Ответственный<em>необязательно</em></span>
              <select name="defect-assignee-id" value={assigneeId} disabled={assignees.loading} onChange={(event) => setAssigneeId(event.target.value)}>
                <option value="">Назначить позже</option>
                <AssigneeOptions people={assignees.data ?? []} objectId={workOrder.object?.id} />
              </select>
              {assignees.error && <small>Список сотрудников недоступен без сети.</small>}
            </label>
          )}
          {photos.length > 0 && (
            <fieldset className="wo-photo-picker">
              <legend>Приложить фотографии</legend>
              <div className="wo-photo-picker__grid">
                {photos.map((photo) => {
                  const checked = photoIds.includes(photo.id)
                  const caption = photo.caption || photo.file.name
                  return (
                    <label key={photo.id} className={checked ? 'wo-photo-pick wo-photo-pick--checked' : 'wo-photo-pick'}>
                      <input name={`defect-photo-${photo.id}`} type="checkbox" checked={checked} onChange={() => setPhotoIds(checked ? photoIds.filter((value) => value !== photo.id) : [...photoIds, photo.id])} />
                      <AuthImage src={photo.url} alt={caption} />
                      <span>{caption}</span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
          )}
        </div>
        <div className="modal__footer">
          <Button type="button" variant="quiet" onClick={onClose} disabled={submitting}>Отмена</Button>
          <Button type="submit" loading={submitting} disabled={!title.trim()}><Wrench size={17} aria-hidden="true" />Создать дефект</Button>
        </div>
      </form>
    </Modal>
  )
}
